import { useMemo } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import useShortlistStore from 'Store/useShortlistStore';

/**
 * useRedeploymentPlanStore
 *
 * Session-only draft of a redeployment reduction plan, keyed by the current
 * role's jd_sb_unique_id. Three screens build one plan up together:
 *
 *   - Redeployment Center Role View saves each role's edited Target
 *     Positions (setTargetPosition);
 *   - Employee View / Reduction Detail add employees "Marked as Available"
 *     under their current role (markEmployeeAvailable — an entry with no
 *     targeted roles yet);
 *   - Candidate Profile's Shortlist appends the shortlisted role to that
 *     employee's targeted_jd_sb_unique_ids (addEmployeeTargetRole — the
 *     ?current_role=... query param carries which plan the append belongs
 *     to), creating the availability entry if the shortlist came first.
 *
 * getRedeploymentPlanPayload assembles the whole draft as the bulk submit
 * payload — both maps keyed by current role JD:
 *
 *   {
 *     plan_name: "...",
 *     available_employee_targets: {
 *       "<current_role_jd>": [
 *         { employee_unique_id: "...", targeted_jd_sb_unique_ids: [],
 *           notifications: { employee: false, line_manager: false, hr_manager: false } },
 *       ],
 *     },
 *     target_positions: { "<current_role_jd>": 2 },
 *   }
 *
 * notifications records the Mark as Available dialog's notify checkboxes
 * (Notify Employee / Line Manager / Hiring Manager) per employee entry, and
 * lands in the stored available_employee_targets JSON as-is. Entries created
 * by the redeployment Shortlist dialogs (Employee View, Reduction Detail,
 * Candidate Profile's Recommend) also carry is_retained and shortlist_reason
 * — the backend requires a non-blank reason whenever is_retained is true.
 *
 * planId/planName (setPlan) track which saved plan this draft belongs to,
 * once it's been saved at least once — see useRedeploymentPlanActions for
 * how the header buttons use them to switch between creating a new plan and
 * updating the same one. loadPlan replaces the whole draft with an existing
 * saved plan's data — View Planning's Edit action calls it before navigating
 * into the Redeployment Center, so editing an old plan reuses the same
 * create/update machinery as a brand-new one.
 *
 * Deliberately NOT persisted to localStorage — same rule as
 * useAvailabilityStore/useShortlistStore: Talent Mobility data is
 * session-only until the backend owns it. Shorter-lived, even: drafts only
 * exist while the user stays inside the redeployment flow — LayoutV2 calls
 * reset() the moment navigation leaves it (see isRedeploymentFlowLocation).
 */
// One flag per notify checkbox on the Mark as Available dialogs — all off
// when an entry is created by a path that didn't ask (e.g. a shortlist from
// a non-redeployment screen).
export const DEFAULT_NOTIFICATIONS = { employee: false, line_manager: false, hr_manager: false };

// A blank reason is stored as null (never ""), so "no reason" has a single
// shape for the table column and CSV to test for. Exported for unit tests.
export const normalizeShortlistReason = (reason) => reason?.trim() || null;

// Maps the Shortlist dialog's context onto the entry keys the backend
// validates. Exported for unit tests.
export const toShortlistFields = ({ isRetained, reason }) => ({
  is_retained: Boolean(isRetained),
  shortlist_reason: normalizeShortlistReason(reason),
});

// Whether an availableEmployeeTargets entry still counts as "available" for
// this current role — the mark/recommend flows' wasAlreadyAvailable checks
// and the Mark as Available tag (useIsEmployeeAvailableForRole) read this,
// not raw entry existence. The Reduction Detail table's Available column and
// its Shortlist/Revert button (useAvailableEmployeeIdsForRole) read raw
// existence instead — once ShortlistAvailabilityButton has created a record
// for an employee under a role, both stay in sync showing "available" until
// that record is fully cleared (unmarkEmployeeAvailable), even if
// isActiveAvailabilityEntry below would call it stale.
// An entry stays in the store even after its last targeted role is reverted
// (removeEmployeeTargetRole below never deletes it — its notifications are
// kept around for the next time this employee is recommended for another
// next role, so the choices don't need re-asking), so existence alone can no
// longer mean "available": a person-first mark (markEmployeeAvailable) sets
// markedAvailable explicitly, since that flow marks someone available before
// any specific next role is chosen; a role-first mark (addEmployeeTargetRole)
// has no such flag and counts on having at least one targeted role instead.
const isActiveAvailabilityEntry = (entry) =>
  Boolean(entry) && (Boolean(entry.markedAvailable) || entry.targeted_jd_sb_unique_ids.length > 0);

const useRedeploymentPlanStore = create(
  devtools(
    (set, get) => ({
      // currentRoleJdId -> target position explicitly edited in Role View
      targetPositions: {},

      // currentRoleJdId -> [{ employee_unique_id, targeted_jd_sb_unique_ids }]
      availableEmployeeTargets: {},

      // currentRoleJdId -> live occupant count, registered by the
      // Redeployment Center once GET /talent-mobility/employees resolves.
      // decrementTargetPosition needs it as the starting target for a role
      // never edited in Role View (a role's default target IS its headcount).
      roleHeadcounts: {},

      // plan_id / plan_name of the saved plan this draft is currently
      // attached to — null/"" for a brand-new, never-saved draft (header
      // shows "New Plan"). Set from the backend's response once a Save Plan
      // / Save & Close Plan popover confirms (see useRedeploymentPlanActions'
      // savePlan): the FIRST save on a fresh draft has no planId, so it
      // POSTs a create; every save after that has one, so it PUTs an update
      // to the same plan instead of creating a duplicate. Cleared by reset(),
      // same as everything else the draft holds.
      planId: null,
      planName: "",

      setPlan: (planId, planName) => set({ planId, planName }),

      // Replaces the whole draft with one saved plan's stored data — the
      // View Planning "Edit" action's counterpart to getRedeploymentPlanPayload,
      // going the other direction: redeploymentPlans is the
      // POST /redeployment-plan/details response's per-role array
      // ({ jd_sb_unique_id, available_employee_targets, target_position }),
      // unpacked back into the two maps this store keys by JD. reset() first
      // so a leftover unsaved draft's shortlist tags don't bleed into the
      // plan being loaded, then every targeted role in the loaded plan is
      // re-seeded into useShortlistStore — ShortlistTag (Redeployment
      // Center's Employee View / At Risk panel) reads purely from that
      // store, so without this a genuinely targeted role would silently
      // show as not shortlisted right after loading.
      loadPlan: ({ planId, planName, redeploymentPlans }) => {
        get().reset();

        const targetPositions = {};
        const availableEmployeeTargets = {};
        const shortlistCandidate = useShortlistStore.getState().shortlistCandidate;
        (redeploymentPlans || []).forEach((role) => {
          const jdId = role.jd_sb_unique_id;
          if (!jdId) return;
          targetPositions[jdId] = role.target_position ?? null;
          const entries = Array.isArray(role.available_employee_targets) ? role.available_employee_targets : [];
          // Only genuinely-available entries ever reach the backend (see
          // getRedeploymentPlanPayload's own filtering below), so anything
          // loaded back from a saved plan is available by definition —
          // stamp markedAvailable regardless of which flow originally
          // created it, or a saved people-first entry with no targeted
          // roles yet would silently stop reading as available after load.
          availableEmployeeTargets[jdId] = entries.map((entry) => ({ ...entry, markedAvailable: true }));
          entries.forEach((entry) =>
            (entry.targeted_jd_sb_unique_ids || []).forEach((targetJdId) =>
              shortlistCandidate(targetJdId, entry.employee_unique_id, false, false, false)
            )
          );
        });

        set({ targetPositions, availableEmployeeTargets, planId, planName });
      },

      setRoleHeadcounts: (headcounts) =>
        set((state) => ({ roleHeadcounts: { ...state.roleHeadcounts, ...headcounts } })),

      setTargetPosition: (currentRoleJdId, targetPosition) =>
        set((state) => {
          const targetPositions = { ...state.targetPositions, [currentRoleJdId]: targetPosition };
          return { targetPositions };
        }),

      // The people-first counterpart to Role View's explicit editing: a
      // confirmed Mark as Available in the Employee View flow (nested panel,
      // or a Candidate Profile entered from it) means one more seat leaves
      // the person's current role, so the role's target walks down by one
      // (floored at 0). One seat per EMPLOYEE, not per mark — callers only
      // invoke this on an employee's first mark for that current role
      // (checked via isEmployeeAvailable before the mark lands); marking the
      // same person for further next roles takes no additional seat. Marks
      // in the target-first Reduction Detail flow deliberately don't call
      // this — there the target was already set and a shortlist leaves the
      // ranking, cutoff and target untouched. No-op until the role's headcount is known
      // (setRoleHeadcounts) or a target was edited.
      decrementTargetPosition: (currentRoleJdId) =>
        set((state) => {
          const effective = state.targetPositions[currentRoleJdId] ?? state.roleHeadcounts[currentRoleJdId];
          if (!Number.isFinite(effective)) return state;
          const targetPositions = {
            ...state.targetPositions,
            [currentRoleJdId]: Math.max(effective - 1, 0),
          };
          return { targetPositions };
        }),

      // Inverse of decrementTargetPosition — un-marking an employee in the
      // people-first flow returns the seat their first mark took. Callers
      // only invoke this when the un-mark removed the employee's LAST
      // targeted role (the availability entry itself), mirroring the
      // first-mark-only decrement, and never from the target-first flow
      // (which never decremented). Capped at the role's headcount so a
      // target that hit decrement's 0 floor can't restore above where it
      // started.
      incrementTargetPosition: (currentRoleJdId) =>
        set((state) => {
          const effective = state.targetPositions[currentRoleJdId] ?? state.roleHeadcounts[currentRoleJdId];
          if (!Number.isFinite(effective)) return state;
          const headcount = state.roleHeadcounts[currentRoleJdId];
          const targetPositions = {
            ...state.targetPositions,
            [currentRoleJdId]: Number.isFinite(headcount) ? Math.min(effective + 1, headcount) : effective + 1,
          };
          return { targetPositions };
        }),

      // shortlistContext ({ isRetained, reason }) is passed by both
      // redeployment Shortlist dialogs (Employee View and Reduction Detail).
      // Employee View always requires a Reason for Shortlisting; Reduction
      // Detail only for a Retained occupant (an At Risk one sends reason
      // null). It lands on the entry as the backend's is_retained /
      // shortlist_reason keys (the API rejects a retained entry with a blank
      // reason). Other callers omit it and their entries carry neither key,
      // exactly as before.
      markEmployeeAvailable: (currentRoleJdId, employeeId, notifications, shortlistContext) =>
        set((state) => {
          const entries = state.availableEmployeeTargets[currentRoleJdId] ?? [];
          const existing = entries.find((entry) => entry.employee_unique_id === employeeId);
          const shortlistFields = shortlistContext ? toShortlistFields(shortlistContext) : {};
          if (existing) {
            // Already marked — a re-confirm only refreshes the notify flags
            // and shortlist reason (and re-activates a stale entry a
            // target-first revert had emptied out, same as a brand-new mark).
            if (!notifications && !shortlistContext) return state;
            const availableEmployeeTargets = {
              ...state.availableEmployeeTargets,
              [currentRoleJdId]: entries.map((entry) =>
                entry.employee_unique_id === employeeId
                  ? {
                      ...entry,
                      markedAvailable: true,
                      notifications: notifications || entry.notifications,
                      ...shortlistFields,
                    }
                  : entry
              ),
            };
            return { availableEmployeeTargets };
          }
          const availableEmployeeTargets = {
            ...state.availableEmployeeTargets,
            [currentRoleJdId]: [
              ...entries,
              {
                employee_unique_id: employeeId,
                targeted_jd_sb_unique_ids: [],
                markedAvailable: true,
                notifications: notifications || { ...DEFAULT_NOTIFICATIONS },
                ...shortlistFields,
              },
            ],
          };
          return { availableEmployeeTargets };
        }),

      // Edits an existing shortlist's notify choices and Reason for
      // Shortlisting — the Reduction Detail "Reason" cell's Edit action. Only
      // those two fields change: is_retained stays as recorded at shortlist
      // time, and availability (markedAvailable, targeted roles) is left
      // alone, so editing never re-activates or reorders anyone. No-op when
      // the employee has no entry under this role.
      updateShortlistDetails: (currentRoleJdId, employeeId, { notifications, reason }) =>
        set((state) => {
          const entries = state.availableEmployeeTargets[currentRoleJdId] ?? [];
          if (!entries.some((entry) => entry.employee_unique_id === employeeId)) return state;
          const availableEmployeeTargets = {
            ...state.availableEmployeeTargets,
            [currentRoleJdId]: entries.map((entry) =>
              entry.employee_unique_id === employeeId
                ? { ...entry, notifications, shortlist_reason: normalizeShortlistReason(reason) }
                : entry
            ),
          };
          return { availableEmployeeTargets };
        }),

      // Reverses markEmployeeAvailable — the Reduction Detail panel's "Revert
      // recommendation" action. Drops the employee's entire entry (plus any
      // targeted-role shortlist tags they'd accumulated since), unlike
      // removeEmployeeTargetRole below which only drops one target role —
      // this undoes the original "Shortlist for Redeployment" recommendation
      // outright. Mirrors markEmployeeAvailable in NOT touching target
      // positions, same as that flow deliberately not calling
      // decrementTargetPosition (see its comment above).
      unmarkEmployeeAvailable: (currentRoleJdId, employeeId) => {
        const entries = get().availableEmployeeTargets[currentRoleJdId] ?? [];
        const existing = entries.find((entry) => entry.employee_unique_id === employeeId);
        if (!existing) return;
        const removeShortlist = useShortlistStore.getState().removeShortlist;
        existing.targeted_jd_sb_unique_ids.forEach((targetJdId) => removeShortlist(targetJdId, employeeId));
        const availableEmployeeTargets = {
          ...get().availableEmployeeTargets,
          [currentRoleJdId]: entries.filter((entry) => entry.employee_unique_id !== employeeId),
        };
        set({ availableEmployeeTargets });
      },

      // Reverse of addEmployeeTargetRole — drops one target role from the
      // employee's entry (plus that pair's session shortlist tag), never the
      // entry itself: employee_unique_id and notifications stay in the store
      // even once targeted_jd_sb_unique_ids empties out, so the notify
      // choices are still there to reuse the next time this employee is
      // recommended for another next role, without asking again. Losing
      // every targeted role does still read as "not available" again
      // everywhere (see isActiveAvailabilityEntry above) — it just no
      // longer erases the record backing that.
      removeEmployeeTargetRole: (currentRoleJdId, employeeId, targetJdId) => {
        const entries = get().availableEmployeeTargets[currentRoleJdId] ?? [];
        const existing = entries.find((entry) => entry.employee_unique_id === employeeId);
        if (!existing?.targeted_jd_sb_unique_ids.includes(targetJdId)) {
          return;
        }
        useShortlistStore.getState().removeShortlist(targetJdId, employeeId);
        const remaining = existing.targeted_jd_sb_unique_ids.filter((id) => id !== targetJdId);
        const nextEntries = entries.map((entry) =>
          entry.employee_unique_id === employeeId ? { ...entry, targeted_jd_sb_unique_ids: remaining } : entry
        );
        const availableEmployeeTargets = { ...get().availableEmployeeTargets, [currentRoleJdId]: nextEntries };
        set({ availableEmployeeTargets });
      },

      // shortlistContext ({ isRetained, reason }) is passed by the Candidate
      // Profile's "Recommend for this role" dialog, which requires a Reason
      // for Shortlisting. A new entry gets both is_retained and
      // shortlist_reason; an existing one only has its reason replaced, so
      // the is_retained recorded by its original shortlist is kept.
      addEmployeeTargetRole: (currentRoleJdId, employeeId, targetJdId, notifications, shortlistContext) =>
        set((state) => {
          const entries = state.availableEmployeeTargets[currentRoleJdId] ?? [];
          const existing = entries.find((entry) => entry.employee_unique_id === employeeId);
          if (existing?.targeted_jd_sb_unique_ids.includes(targetJdId)) {
            return state;
          }
          const nextEntries = existing
            ? entries.map((entry) =>
                entry.employee_unique_id === employeeId
                  ? {
                      ...entry,
                      targeted_jd_sb_unique_ids: [...entry.targeted_jd_sb_unique_ids, targetJdId],
                      notifications: notifications || entry.notifications || { ...DEFAULT_NOTIFICATIONS },
                      ...(shortlistContext && { shortlist_reason: normalizeShortlistReason(shortlistContext.reason) }),
                    }
                  : entry
              )
            : [
                ...entries,
                {
                  employee_unique_id: employeeId,
                  targeted_jd_sb_unique_ids: [targetJdId],
                  notifications: notifications || { ...DEFAULT_NOTIFICATIONS },
                  ...(shortlistContext && toShortlistFields(shortlistContext)),
                },
              ];
          const availableEmployeeTargets = { ...state.availableEmployeeTargets, [currentRoleJdId]: nextEntries };
          return { availableEmployeeTargets };
        }),

      // Scoped version of reset() below — drops ONE role's drafted employee
      // data (marked-available entries plus their session shortlist tags),
      // leaving every other role's draft and the role's own target position
      // untouched. Role View calls this when a role's Target Positions commits
      // to a different value: the entries were picked against the old at-risk
      // cutoff, so the role's reduction detail must start over from zero.
      resetRoleDraft: (currentRoleJdId) => {
        const entries = get().availableEmployeeTargets[currentRoleJdId];
        if (!entries?.length) return;
        const removeShortlist = useShortlistStore.getState().removeShortlist;
        entries.forEach((entry) =>
          entry.targeted_jd_sb_unique_ids.forEach((targetJdId) =>
            removeShortlist(targetJdId, entry.employee_unique_id)
          )
        );
        const availableEmployeeTargets = { ...get().availableEmployeeTargets };
        delete availableEmployeeTargets[currentRoleJdId];
        set({ availableEmployeeTargets });
      },

      isEmployeeAvailable: (currentRoleJdId, employeeId) =>
        isActiveAvailabilityEntry(
          (get().availableEmployeeTargets[currentRoleJdId] ?? []).find(
            (entry) => entry.employee_unique_id === employeeId
          )
        ),

      // Raw entry existence, NOT isActiveAvailabilityEntry — same rule
      // useAvailableEmployeeIdsForRole reads. An entry's notifications are set
      // the moment it's first created (markEmployeeAvailable/
      // addEmployeeTargetRole always pass a notifications object) and never
      // unset again, so "has a record" also means "notify choices already
      // captured". (The Candidate Profile's Recommend shortcut additionally
      // needs a saved shortlist_reason before it skips its dialog.)
      hasAvailabilityRecord: (currentRoleJdId, employeeId) =>
        Boolean(
          (get().availableEmployeeTargets[currentRoleJdId] ?? []).some(
            (entry) => entry.employee_unique_id === employeeId
          )
        ),

      // Assembles the bulk POST /talent-mobility/redeployment-plan payload —
      // keyed by current role JD, labeled with the submitting screen's plan
      // name. Only still-active entries are sent (a stale entry
      // removeEmployeeTargetRole kept around purely to preserve its
      // notifications for reuse is client-only bookkeeping and has nothing
      // for the backend to act on); a role left with none is dropped
      // entirely, same as when its last employee was fully un-marked.
      getRedeploymentPlanPayload: (planName) => {
        const { targetPositions, availableEmployeeTargets } = get();
        const activeEmployeeTargets = Object.fromEntries(
          Object.entries(availableEmployeeTargets)
            .map(([jdId, entries]) => [jdId, entries.filter(isActiveAvailabilityEntry)])
            .filter(([, entries]) => entries.length)
        );
        const payload = {
          plan_name: planName,
          available_employee_targets: activeEmployeeTargets,
          target_positions: { ...targetPositions },
        };
        return payload;
      },

      reset: () => {
        // Shortlists made inside the flow also live in useShortlistStore (so
        // ShortlistTag shows them on every table) — take those pairs back
        // too, or the "Shortlisted" tag would outlive the plan it belonged
        // to. The plan itself records exactly which pairs those were:
        // (targeted_jd_sb_unique_id, employee_unique_id).
        const { availableEmployeeTargets } = get();
        const removeShortlist = useShortlistStore.getState().removeShortlist;
        Object.values(availableEmployeeTargets).forEach((entries) =>
          entries.forEach((entry) =>
            entry.targeted_jd_sb_unique_ids.forEach((targetJdId) =>
              removeShortlist(targetJdId, entry.employee_unique_id)
            )
          )
        );
        set({
          targetPositions: {},
          availableEmployeeTargets: {},
          roleHeadcounts: {},
          planId: null,
          planName: "",
        });
      },
    }),
    { name: 'RedeploymentPlanStore' }
  )
);

/**
 * Whether a router location is inside the redeployment flow: the
 * Redeployment Center screens (list + reduction detail), PAIC Org Redesign's
 * reduction detail (same page, its own route), or the Candidate
 * Profile when entered from them (their View Profile buttons append
 * ?current_role=... — the same profile page reached from Role Detail or
 * Career Pathways carries no such param and is NOT part of the flow).
 * LayoutV2 watches route changes and resets this store as soon as
 * navigation moves from an in-flow location to anywhere else.
 */
export const isRedeploymentFlowLocation = ({ pathname, search }) =>
  pathname.startsWith('/talent-mobility/redeployment-center') ||
  // PAIC Org Redesign's own entry onto the same Reduction Detail page — it
  // writes ?target= into this store, so leaving it must reset the draft too.
  pathname.startsWith('/talent-mobility/paic-org-redesign/reduction/') ||
  (pathname.startsWith('/talent-mobility/candidate/') && new URLSearchParams(search).has('current_role'));

/**
 * PAIC Org Redesign's reduction detail, and a Candidate Profile opened from
 * it (?paic_version_id=), borrow this store as a scratchpad for one PAIC
 * role. Leaving them — even straight into the Redeployment Center — must
 * reset it, so that role never shows up as a redeployment draft.
 */
export const isPaicReductionLocation = ({ pathname, search }) =>
  pathname.startsWith('/talent-mobility/paic-org-redesign/reduction/') ||
  (pathname.startsWith('/talent-mobility/candidate/') && new URLSearchParams(search).has('paic_version_id'));

// Stable selectors — same pattern as useIsShortlisted, avoiding the "new
// object every call" trap that caused useMatchThresholds' infinite-loop bug.
// Whether any plan data has been drafted — the same shape as
// getRedeploymentPlanPayload's nothing-to-submit guard (a stale, fully
// un-targeted entry removeEmployeeTargetRole kept around for its
// notifications doesn't count as a draft on its own, same as a role with no
// active entries at all). Drives the redeployment screens' Submit button
// enabled state.
export const useHasRedeploymentPlanDraft = () => {
  const targetPositions = useRedeploymentPlanStore((state) => state.targetPositions);
  const availableEmployeeTargets = useRedeploymentPlanStore((state) => state.availableEmployeeTargets);
  return useMemo(
    () =>
      Object.keys(targetPositions).length > 0 ||
      Object.values(availableEmployeeTargets).some((entries) => entries.some(isActiveAvailabilityEntry)),
    [targetPositions, availableEmployeeTargets]
  );
};

export const useIsEmployeeAvailableForRole = (currentRoleJdId, employeeId) => {
  const entries = useRedeploymentPlanStore((state) => state.availableEmployeeTargets[currentRoleJdId]);
  return useMemo(
    () => isActiveAvailabilityEntry(entries?.find((entry) => entry.employee_unique_id === employeeId)),
    [entries, employeeId]
  );
};

// Raw entry existence, NOT isActiveAvailabilityEntry — matches
// ShortlistAvailabilityButton's own check, so the Reduction Detail table's
// Available column always agrees with its Shortlist/Revert button for the
// same row (an entry left with no markedAvailable and no targeted roles
// still reads as available here, since the button still offers "Revert
// recommendation" for it).
export const useAvailableEmployeeIdsForRole = (currentRoleJdId) => {
  const entries = useRedeploymentPlanStore((state) => state.availableEmployeeTargets[currentRoleJdId]);
  return useMemo(
    () => new Set((entries ?? []).map((entry) => entry.employee_unique_id)),
    [entries]
  );
};

// employee_unique_id -> Reason for Shortlisting, for every entry under this
// role that carries one — the Reduction Detail's "Reason for Shortlisting"
// column and its CSV read it without a hook call per row. Entries loaded
// from a saved plan keep their shortlist_reason (loadPlan spreads entries
// as-is), so reasons survive a View Planning "Edit" round trip.
export const useShortlistReasonsForRole = (currentRoleJdId) => {
  const entries = useRedeploymentPlanStore((state) => state.availableEmployeeTargets[currentRoleJdId]);
  return useMemo(
    () =>
      new Map(
        (entries ?? [])
          .filter((entry) => entry.shortlist_reason)
          .map((entry) => [entry.employee_unique_id, entry.shortlist_reason])
      ),
    [entries]
  );
};

// All target role ids on the employee's targeted list, as a Set — lets the
// Employee View's next-roles table tag each row that was marked available
// from its Candidate Profile without a hook call per row.
export const useTargetedRoleIdsForEmployee = (currentRoleJdId, employeeId) => {
  const entries = useRedeploymentPlanStore((state) => state.availableEmployeeTargets[currentRoleJdId]);
  return useMemo(
    () =>
      new Set(
        entries?.find((entry) => entry.employee_unique_id === employeeId)?.targeted_jd_sb_unique_ids ?? []
      ),
    [entries, employeeId]
  );
};

// Whether one specific target role is on the employee's targeted list —
// drives the Candidate Profile's Mark as Available/Unavailable toggle, which
// is scoped to the role being viewed (an employee marked available for role A
// still shows "Mark as Available" on role B's profile).
export const useIsRoleTargetedForEmployee = (currentRoleJdId, employeeId, targetJdId) => {
  const entries = useRedeploymentPlanStore((state) => state.availableEmployeeTargets[currentRoleJdId]);
  return useMemo(
    () =>
      Boolean(
        entries
          ?.find((entry) => entry.employee_unique_id === employeeId)
          ?.targeted_jd_sb_unique_ids.includes(targetJdId)
      ),
    [entries, employeeId, targetJdId]
  );
};


export default useRedeploymentPlanStore;
